import { useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { PostCard } from '@/desktop/components/PostCard';
import type { NostrEvent } from '@nostrify/nostrify';

/** Start loading the next page when the user scrolls within this many px of the bottom. */
const LOAD_MORE_MARGIN = 600;

/**
 * Scrollable feed body shared by the Feed tabs: renders PostCards and loads
 * the next page as the user approaches the bottom.
 */
export function FeedList({
  events,
  onOpenThread,
  onOpenProfile,
  emptyMessage,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  events: NostrEvent[];
  onOpenThread: (event: NostrEvent) => void;
  onOpenProfile: (pubkey: string) => void;
  emptyMessage: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}) {
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_MARGIN) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="flex-1 overflow-auto min-h-0" onScroll={handleScroll}>
      {events.length > 0 ? (
        <>
          {events.map((event) => (
            <PostCard key={event.id} event={event} onOpenThread={onOpenThread} onOpenProfile={onOpenProfile} />
          ))}
          {isFetchingNextPage && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {!hasNextPage && (
            <div className="py-4 text-center text-[11px] text-muted-foreground">
              You're all caught up
            </div>
          )}
        </>
      ) : (
        <div className="p-8 text-center text-muted-foreground text-sm">{emptyMessage}</div>
      )}
    </div>
  );
}
